import { Empty } from "antd";
import { FinanceSettings } from "./finance-settings";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { ComponentQueryLoader } from "../../../shared/component-query-loader";
import {
  BlobFinanceConfigInput,
  BlobFinanceConfigNameInput,
  StaffFinanceSettingsContainerBlobFinanceConfigFieldsFragment,
  StaffFinanceSettingsContainerBlobFinancialConfigFieldsFragment,
  StaffFinanceSettingsContainerBlobFinancialConfigsDocument,
  StaffFinanceSettingsContainerBlobFinancialConfigsQuery,
  StaffFinanceSettingsContainerBlobFinancialGlConfigDocument,
  StaffFinanceSettingsContainerStaffUserFieldsFragment,
  StaffFinanceSettingsContainerStaffUsersDocument,
  StaffFinanceSettingsContainerUpdateBlobFinanceGlConfigDocument,
  StaffFinanceSettingsContainerUpdateBlobFinanceGlConfigMutationVariables,
} from "../../../../generated";

interface FinanceSettingsContainerProps {}

export const FinanceSettingsContainer: React.FC<FinanceSettingsContainerProps> = () => {
  // You can add any state or logic here if needed
  const { data: financeConfigsData, loading: financeConfigsLoading, error: financeConfigsError } = useQuery(StaffFinanceSettingsContainerBlobFinancialConfigsDocument);
  const { data: staffUsersData, loading: staffUsersLoading, error: staffUsersError } = useQuery(StaffFinanceSettingsContainerStaffUsersDocument);
  const [getFinanceConfig] = useLazyQuery(StaffFinanceSettingsContainerBlobFinancialGlConfigDocument);
  const [blobFinanceSettingsUpdate] = useMutation(StaffFinanceSettingsContainerUpdateBlobFinanceGlConfigDocument);

  const fetchFinanceConfig = async (blobId: string): Promise<StaffFinanceSettingsContainerBlobFinanceConfigFieldsFragment> => {
    const input: BlobFinanceConfigNameInput = {
      configBlobName: blobId,
    };
    const result = await getFinanceConfig({ variables: { input } });
    return result?.data?.blobFinancialGlConfig as StaffFinanceSettingsContainerBlobFinanceConfigFieldsFragment;
  };

  const getNextVersion = (caseType: string) => {
    const financeConfigs = financeConfigsData?.blobFinancialConfigs?.filter((config) => config?.caseType == caseType);
    const maxVersion = financeConfigs?.reduce((max, config) => {
      const version = parseFloat(config?.version ?? "0");
      return version > max ? version : max;
    }, 0);
    const nextVersion = maxVersion ? maxVersion + 0.1 : 1.0;
    return nextVersion.toFixed(1);
  };

  const handleSave = async (inputData: BlobFinanceConfigInput) => {
    if (!inputData.id && inputData?.blobData?.glConfig && inputData?.caseType) {
      inputData.blobData.glConfig.version = getNextVersion(inputData.caseType).toString();
    }

    const financeConfig: StaffFinanceSettingsContainerUpdateBlobFinanceGlConfigMutationVariables = {
      input: inputData,
    };

    await blobFinanceSettingsUpdate({
      variables: financeConfig,
      update: (cache, { data }) => {
        if (!data?.updateBlobFinanceGlConfig) {
          return;
        }
        const updatedConfig = data.updateBlobFinanceGlConfig.financeGlConfig;
        const existingConfigs = financeConfigsData?.blobFinancialConfigs ?? [];
        const configIndex = existingConfigs.findIndex((config) => config?.id === updatedConfig.id);
        const newData: StaffFinanceSettingsContainerBlobFinancialConfigsQuery = {
          blobFinancialConfigs:
            configIndex > -1
              ? existingConfigs.map((config, index) => (index === configIndex ? { ...config, effectiveAt: updatedConfig.effectiveAt, ruleName: updatedConfig.glConfig?.note } : config))
              : [
                  {
                    caseType: inputData.caseType,
                    effectiveAt: updatedConfig.effectiveAt,
                    ruleName: updatedConfig.glConfig?.note,
                    version: updatedConfig.glConfig?.version,
                    id: updatedConfig.id,
                    __typename: "BlobFinancialConfig",
                  },
                  ...existingConfigs,
                ],
        };
        cache.writeQuery({
          query: StaffFinanceSettingsContainerBlobFinancialConfigsDocument,
          data: newData,
        });
      },
    });
  };

  return (
    <ComponentQueryLoader
      loading={financeConfigsLoading || staffUsersLoading}
      error={financeConfigsError || staffUsersError}
      hasData={!!financeConfigsData && !!staffUsersData}
      hasDataComponent={
        <FinanceSettings
          data={financeConfigsData?.blobFinancialConfigs as StaffFinanceSettingsContainerBlobFinancialConfigFieldsFragment[]}
          staffUsers={staffUsersData?.staffUsers as StaffFinanceSettingsContainerStaffUserFieldsFragment[]}
          onSave={handleSave}
          onRowClick={fetchFinanceConfig}
        />
      }
      noDataComponent={<Empty description="No data available" />}
    />
  );
};
